import React, { useState } from "react";
import { Checkbox, FormControlLabel, IconButton, TextField, Box, Typography, Button, Menu, MenuItem, CircularProgress, Stack, Card, Grid, AccordionSummary, Accordion, AccordionDetails } from "@mui/material";
import { Add, Close, ArrowDropDown, ExpandMore } from "@mui/icons-material";
import { parseTimeToMinutes, TimePicker } from "./TimePicker";
import MDButton from "components/MDButton";
import { openSnackbar } from "../../../redux/slices/common";
import { addSlotsDays } from "../../../redux/slices/hyperwolf/settings";
import { useSelector } from "react-redux";
import AngleRightIcon from 'common/customIcons/angle_right';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";

const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

export default function CreateTimeSlots() {
    const [formData, setFormData] = useState({
        days: [] // { day: string, slots: [...] }
    });
    // Changed validation structure to track slot-level errors
    const [validationErrors, setValidationErrors] = useState({}); // { dayName: { slotIndex: { field: boolean } } }
    const { user } = useSelector(state => state.auth);

    const dispatch = useDispatch()
    const { isLoading } = useSelector(state => state.hyperwolfSettings);
    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState(null);
    const [repeatDays, setRepeatDays] = useState([]);
    const [repeatSourceDay, setRepeatSourceDay] = useState(null);

    const validateDaySlots = (day) => {
        const dayObj = formData?.days.find(d => d?.day === day);
        if (!dayObj) return false;

        return dayObj?.slots.every(slot =>
            slot?.startTime &&
            slot?.endTime &&
            slot?.overallOrderLimit
        );
    };

    // Helper function to check if a specific slot has errors
    const hasSlotError = (day, slotIndex) => {
        return validationErrors[day] && validationErrors[day][slotIndex] &&
            Object.values(validationErrors[day][slotIndex]).some(error => error);
    };

    const handleDayToggle = (day) => {
        setFormData((prev) => {
            const exists = prev.days.find((d) => d?.day === day);
            if (exists) {
                return {
                    ...prev,
                    days: prev.days.filter((d) => d?.day !== day),
                };
            } else {
                return {
                    ...prev,
                    days: [
                        ...prev.days,
                        { day, slots: [{ startTime: "", endTime: "", overallOrderLimit: "" }] }
                    ],
                };
            }
        });
        // Clear validation errors for this day when toggling
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[day];
            return newErrors;
        });
    };
    const handleSlotChange = (day, index, field, value) => {
        setFormData(prev => ({
            ...prev,
            days: prev.days.map(d =>
                d.day === day
                    ? {
                        ...d,
                        slots: d.slots.map((slot, i) => {
                            if (i !== index) return slot;

                            // Reset endTime when startTime is changed
                            const updatedSlot = {
                                ...slot,
                                [field]: value,
                            };
                            if (field === "startTime") {
                                updatedSlot.endTime = "";
                            }
                            return updatedSlot;
                        }),
                    }
                    : d
            ),
        }));

        // Clear validation error for this specific field when editing
        if (value) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                if (newErrors[day] && newErrors[day][index]) {
                    delete newErrors[day][index][field];

                    // If no more field errors for this slot, remove the slot error
                    if (Object.keys(newErrors[day][index]).length === 0) {
                        delete newErrors[day][index];

                        // If no more slot errors for this day, remove the day error
                        if (Object.keys(newErrors[day]).length === 0) {
                            delete newErrors[day];
                        }
                    }
                }
                return newErrors;
            });
        }
    };

    const addSlot = (day) => {
        setFormData((prev) => ({
            ...prev,
            days: prev.days.map((d) =>
                d.day === day
                    ? {
                        ...d,
                        slots: [...d.slots, { startTime: "", endTime: "", overallOrderLimit: "" }],
                    }
                    : d
            ),
        }));
    };

    const removeSlot = (day, index) => {
        setFormData((prev) => {
            const updatedDays = prev.days.map((d) => {
                if (d?.day === day) {
                    return {
                        ...d,
                        slots: d?.slots.filter((_, i) => i !== index),
                    };
                }
                return d;
            });

            return {
                ...prev,
                days: updatedDays.filter((d) => d?.slots.length > 0),
            };
        });

        // Clear validation errors for the removed slot
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            if (newErrors[day] && newErrors[day][index]) {
                delete newErrors[day][index];

                // Reindex remaining slot errors
                const dayErrors = newErrors[day];
                const reindexedErrors = {};
                Object.keys(dayErrors).forEach(slotIdx => {
                    const numIdx = parseInt(slotIdx);
                    if (numIdx < index) {
                        reindexedErrors[numIdx] = dayErrors[numIdx];
                    } else if (numIdx > index) {
                        reindexedErrors[numIdx - 1] = dayErrors[numIdx];
                    }
                });

                if (Object.keys(reindexedErrors).length === 0) {
                    delete newErrors[day];
                } else {
                    newErrors[day] = reindexedErrors;
                }
            }
            return newErrors;
        });
    };

    const handleRepeatSlots = (sourceDay, targetDays) => {
        setFormData((prev) => {
            const sourceDayData = prev.days.find((d) => d?.day === sourceDay);
            if (!sourceDayData) return prev;

            return {
                ...prev,
                days: prev.days.map((d) =>
                    targetDays.includes(d?.day)
                        ? {
                            ...d,
                            slots: [...d.slots, ...sourceDayData.slots],
                        }
                        : d
                ),
            };
        });
        showError(`Slots Applied Successfully`, 'success');
    };

    const openRepeatMenu = (event, sourceDay) => {
        if (!validateDaySlots(sourceDay)) {
            showError(`Please fill all slot fields for ${sourceDay.toLowerCase()} before repeating.`, 'error');
            return;
        }
        setAnchorEl(event.currentTarget);
        setRepeatSourceDay(sourceDay);
        setRepeatDays([]);
    };

    const closeRepeatMenu = () => {
        setAnchorEl(null);
        setRepeatSourceDay(null);
    };

    const selectedDaysList = formData?.days.map(d => d.day);

    const handleSubmit = (e) => {
        e.preventDefault();
        const errors = {};
        let hasErrors = false;

        if (formData?.days.length === 0) {
            showError("Please select at least one day and create slot(s).", 'error');
            return;
        }

        // Updated validation logic for slot-level errors
        for (const dayObj of formData.days) {
            dayObj.slots.forEach((slot, slotIndex) => {
                const slotErrors = {};

                if (!slot.startTime) {
                    slotErrors.startTime = true;
                    hasErrors = true;
                }
                if (!slot.endTime) {
                    slotErrors.endTime = true;
                    hasErrors = true;
                }
                if (!slot.overallOrderLimit) {
                    slotErrors.overallOrderLimit = true;
                    hasErrors = true;
                }

                if (Object.keys(slotErrors).length > 0) {
                    if (!errors[dayObj.day]) {
                        errors[dayObj.day] = {};
                    }
                    errors[dayObj.day][slotIndex] = slotErrors;
                }
            });
        }

        setValidationErrors(errors);

        if (hasErrors) {
            showError("Please fill all required fields before submitting.", 'error');
            return;
        }

        const payload = formData.days.map((d) => ({
            updatedBy: user?.name,
            day: d.day,
            slots: d.slots.map((s) => ({
                startTime: s?.startTime,
                endTime: s?.endTime,
                overallOrderLimit: Number(s?.overallOrderLimit),
            })),
        }));

        dispatch(addSlotsDays(payload)).unwrap().then((res) => {
            showError(res?.message, 'success');
            navigate(-1);
        }).catch((err) => {
            showError(err?.message, 'error');
        });
    }

    const showError = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
        }
    };

    return (
        <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }} className="create-time-slots">
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="stretch" gap={2} mb={2}>
                <span onClick={() => navigate(-1)} className='cur-pointer'>
                    <Box display="flex" gap={1} alignItems="center" color='text.main' lineHeight={1}>
                        <AngleRightIcon fontSize="14px" />
                        <Typography variant="h6" fontWeight="semibold" ml={0.75}>
                            Time Slot Management
                        </Typography>
                    </Box>
                </span>
            </Stack>

            <Box p={{ xs: 0 }}>
                <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12} lg={12}>
                            <Typography variant='h5' fontWeight="semibold" color="secondary">Create Time Slot</Typography>
                        </Grid>

                        <Grid item xs={12} md={12} lg={12}>
                            <Typography variant='h6' fontWeight="semibold" color="#2E2E3A" mb={0.5}>Select Days</Typography>
                            <Stack direction="row" flexWrap="wrap" alignItems="stretch" gap={2} mb={2} className="select-days-sec">
                                {/* Select All / Deselect All checkbox */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.days.length === daysOfWeek.length}
                                            indeterminate={
                                                formData.days.length > 0 &&
                                                formData.days.length < daysOfWeek.length
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    // Select all days
                                                    const allDays = daysOfWeek.map(day => ({
                                                        day,
                                                        slots: [{ startTime: "", endTime: "", overallOrderLimit: "" }]
                                                    }));
                                                    setFormData({ days: allDays });
                                                } else {
                                                    // Deselect all days
                                                    setFormData({ days: [] });
                                                }
                                                // Clear all validation errors
                                                setValidationErrors({});
                                            }}
                                        />
                                    }
                                    label={formData.days.length === daysOfWeek.length ? "Select All" : "Select All"}
                                />

                                {/* Individual day checkboxes */}
                                {daysOfWeek.map((day) => (
                                    <FormControlLabel
                                        key={day}
                                        control={
                                            <Checkbox
                                                checked={formData.days.some((d) => d.day === day)}
                                                onChange={() => handleDayToggle(day)}
                                            />
                                        }
                                        label={day}
                                    />
                                ))}
                            </Stack>
                        </Grid>

                        {/* <Typography variant='h6' px={3} py={1} fontWeight="semibold" color="#2E2E3A">Add Time</Typography> */}

                        {formData?.days
                            .slice()
                            .sort((a, b) => daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day))
                            .map((dayObj) => (
                                <Grid item xs={12} md={12} lg={12} key={dayObj.day}>
                                    <Accordion 
                                        sx={{
                                            borderRadius: 2.5,
                                            boxShadow: 'none',
                                            background: "#f5f5ff99",
                                            '&:before': { display: 'none' }
                                        }}
                                        className="shop-timing-accordion"
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            aria-controls={`${dayObj.day}-content`}
                                            id={`${dayObj.day}-header`}
                                            sx={{
                                                '& .MuiAccordionSummary-content': {
                                                    alignItems: 'center',
                                                    gap: '20px'
                                                }
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                color="#2E2E3A"
                                            >
                                                {dayObj?.day}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                style={{ textTransform: "none", color: "#0163FF" }}
                                                endIcon={<ArrowDropDown />}
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent accordion toggle
                                                    openRepeatMenu(e, dayObj.day);
                                                }}
                                            >
                                                Repeat this slot on
                                            </Button>
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            {dayObj?.slots.map((slot, index) => (
                                                <Box
                                                    key={index}
                                                    className={hasSlotError(dayObj.day, index) ? 'time-sec-rep error-show' : 'time-sec-rep'}
                                                    sx={{ mb: 2 }}
                                                >
                                                    <Box className="time-card-rep">
                                                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "5px" }}>
                                                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px" }}>
                                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: "2px" }}>
                                                                    <Typography variant='body2' fontWeight="500" mb={0.5} color="#9A9AAF" style={{ width: "100%" }}>Open Time</Typography>
                                                                    <TimePicker
                                                                        value={slot?.startTime}
                                                                        // placeHolder={'Start Time'}
                                                                        onChange={(val) =>
                                                                            handleSlotChange(dayObj?.day, index, "startTime", val)
                                                                        }
                                                                        error={validationErrors[dayObj.day]?.[index]?.startTime}
                                                                    />
                                                                </Box>
                                                                <span style={{ position: "relative", top: "12px", color: "#2E2E3A", margin: "0 5px" }}>-</span>
                                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "start", gap: "2px" }}>
                                                                    <Typography variant='body2' fontWeight="500" mb={0.5} color="#9A9AAF" style={{ width: "100%" }}>Close Time</Typography>
                                                                    <TimePicker
                                                                        value={slot?.endTime}
                                                                        // placeHolder={'End Time'}
                                                                        onChange={(val) =>
                                                                            handleSlotChange(dayObj?.day, index, "endTime", val)
                                                                        }
                                                                        disableBeforeMinutes={
                                                                            slot.startTime ? parseTimeToMinutes(slot?.startTime) : undefined
                                                                        }
                                                                        error={validationErrors[dayObj.day]?.[index]?.endTime}
                                                                    />
                                                                </Box>
                                                            </Box>

                                                            <Stack direction="row" flexWrap="wrap" alignItems="stretch" gap={1.5} className="close-add-icons">
                                                                {(
                                                                    <IconButton onClick={() => removeSlot(dayObj?.day, index)}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                                                            <path d="M5.5 16.5L16.5 5.5M5.5 5.5L16.5 16.5" stroke="#2E2E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    </IconButton>
                                                                )}
                                                                {index === dayObj?.slots.length - 1 && (
                                                                    <IconButton onClick={() => addSlot(dayObj?.day)}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                                                            <path d="M11 8.25V13.75M13.75 11H8.25M19.25 11C19.25 12.0834 19.0366 13.1562 18.622 14.1571C18.2074 15.1581 17.5997 16.0675 16.8336 16.8336C16.0675 17.5997 15.1581 18.2074 14.1571 18.622C13.1562 19.0366 12.0834 19.25 11 19.25C9.91659 19.25 8.8438 19.0366 7.84286 18.622C6.84193 18.2074 5.93245 17.5997 5.16637 16.8336C4.40029 16.0675 3.7926 15.1581 3.37799 14.1571C2.96339 13.1562 2.75 12.0834 2.75 11C2.75 8.81196 3.61919 6.71354 5.16637 5.16637C6.71354 3.61919 8.81196 2.75 11 2.75C13.188 2.75 15.2865 3.61919 16.8336 5.16637C18.3808 6.71354 19.25 8.81196 19.25 11Z" stroke="#2E2E3A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    </IconButton>
                                                                )}
                                                            </Stack>
                                                        </Box>                                                    

                                                        <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
                                                            <Typography variant='body2' fontWeight="500" mb={0.5} color="#9A9AAF" style={{ width: "100%" }}>Max Qty of Orders Per Slot</Typography>
                                                            <TextField
                                                                style={{ maxWidth: "320px" }}
                                                                type="text"
                                                                value={slot.overallOrderLimit}
                                                                onChange={(e) => {
                                                                    const numbersOnly = e.target.value.replace(/\D/g, '');
                                                                    handleSlotChange(dayObj.day, index, "overallOrderLimit", numbersOnly);
                                                                }}
                                                                inputProps={{
                                                                    inputMode: 'numeric',
                                                                    pattern: '[0-9]*',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            ))}

                        <Grid item xs={12} md={12} lg={12}>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={closeRepeatMenu}
                                PaperProps={{ sx: { p: 1 } }}
                                className="repeat-slot-dropdown"
                            >
                                <Typography variant="body2" fontWeight="semibold" sx={{ px: 1, pb: 1 }} color="#2E2E3A">
                                    Copy Times To...
                                </Typography>
                                {daysOfWeek?.map((day) => {
                                    const isSameDay = day === repeatSourceDay;
                                    const isDisabled = isSameDay || !selectedDaysList.includes(day);

                                    return (
                                        <MenuItem
                                            key={day}
                                            sx={{
                                                py: 0,
                                                color: isDisabled ? "text.disabled" : "text.primary",
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        disabled={isDisabled}
                                                        checked={
                                                            isSameDay
                                                                ? true
                                                                : repeatDays.includes(day)
                                                        }
                                                        onChange={(e) => {
                                                            if (isSameDay) return;
                                                            if (e.target.checked) {
                                                                setRepeatDays([...repeatDays, day]);
                                                            } else {
                                                                setRepeatDays(repeatDays.filter(d => d !== day));
                                                            }
                                                        }}
                                                    />
                                                }
                                                label={day}
                                                sx={{ color: isDisabled ? "text.disabled" : "text.primary" }}
                                            />
                                        </MenuItem>
                                    );
                                })}
                                <Box sx={{ px: 1, pt: 1, pb: 1 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="small"
                                        onClick={() => {
                                            handleRepeatSlots(repeatSourceDay, repeatDays);
                                            closeRepeatMenu();
                                        }}
                                        style={{ lineHeight: "22px" }}
                                        disabled={repeatDays?.length === 0}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            <Box p={{ xs: 0 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={12}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} mt={4} mb={4}>
                            <MDButton
                                sx={{ minWidth: '150px' }}
                                type='submit'
                                variant='contained'
                                onClick={handleSubmit}
                                color={'primary'}
                                disabled={isLoading}
                            >
                                {isLoading ? <CircularProgress size={14} justifyContent="center" style={{ color: "#ffffff" }} /> : 'Save'}
                            </MDButton>
                            <MDButton
                                sx={{ minWidth: '150px' }}
                                type='button'
                                variant='outlined'
                                color='dark'
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </MDButton>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}